import { FormCard, FormItem, Input, Select, TextArea } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { InputNumber } from 'antd'
import type { DefaultOptionType } from 'antd/es/select'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useGroupedCarers } from 'hooks/useGroupedOptions'
import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PatternSelectEmptyState from '../components/PatternSelectEmptyState'
import useAllocationList from '../hooks/useAllocationList'
import { useCarersClockedInValidator } from '../hooks/useCarersClockedInValidator'
import { getRequiredRule, getTeamMembersClockInsRule } from '../utils'
import InputSuffix from './InputSuffix'
import { TeamMembersMultiSelect } from './TeamMembersMultiSelect'
import {
  cleanDecimalValues,
  convertAllocationListToGroupedOptions
} from './utils'

type AboutShiftProps = {
  typeOfShiftOptions?: DefaultOptionType[]
  hideTeamMembers?: boolean
  hideAboutSection?: boolean
  showTeamMembersSelect?: boolean
  formData?: Record<string, any> | undefined
}

const AboutShift: FC<AboutShiftProps> = ({
  typeOfShiftOptions,
  hideTeamMembers,
  hideAboutSection,
  showTeamMembersSelect = false,
  formData
}) => {
  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: ['rostering_editing_limitations']
  })?.data

  const flagsRosteringEditingLimitations =
    flags?.rostering_editing_limitations?.enabled

  const instanceId = formData?.id || formData?.eventId

  // Use allocation list API (like quick allocation) to get users with clock-in status
  // This automatically filters out clocked-in users when availability filter is used
  const { allocationList, isListLoading } = useAllocationList({
    id: instanceId,
    allocationListFilters: flagsRosteringEditingLimitations
      ? {
          availability: ['Available and Selected']
        }
      : undefined,
    skipPagination: true
  })

  // Fallback to grouped carers if no instanceId (new events)
  const { groupedCarerOptions: fallbackGroupedCarerOptions } =
    useGroupedCarers()

  const groupedCarerOptions = useMemo(() => {
    // A new event has no instance to allocate against, so the carer list is all there is.
    // The hook is disabled without an id, and react-query reports a disabled query as
    // loading, so this branch has to come first.
    if (!instanceId) {
      return fallbackGroupedCarerOptions
    }

    // ROST-3866. For an existing event the allocation list is the only valid source — while
    // it loads, if it fails (no data), and when it is genuinely empty. Falling back here put
    // the whole carer list back, INACTIVE group and people no longer at the home included.
    if (isListLoading || !allocationList?.length) {
      return []
    }

    return convertAllocationListToGroupedOptions(allocationList, [])
  }, [allocationList, fallbackGroupedCarerOptions, instanceId, isListLoading])

  const teamMembersCount = formData?.teamMembers?.length ?? 0
  const maxTeamMembers = formData?.numberSupply ?? 0

  // Get original team member IDs from formData (set in EditReqModal)
  const originalTeamMemberIds = formData?.originalTeamMemberIds

  // Use formData?.id or formData?.eventId, and pass original team members
  const { validator: validateTeamMembers } = useCarersClockedInValidator(
    formData?.id || formData?.eventId,
    originalTeamMemberIds
  )

  return (
    <FormCard id="rota-about-shift-form" level={2} title={t('ABOUT_THE_SHIFT')}>
      {!hideAboutSection && (
        <>
          <FormItem
            name="instanceTypeId"
            label={<Text>{t('TYPE_OF_SHIFT')}</Text>}
            rules={[getRequiredRule(t('TYPE_OF_SHIFT'))]}
          >
            <Select
              options={typeOfShiftOptions}
              notFoundContent={
                <PatternSelectEmptyState link="/settings/rota-patterns" />
              }
            />
          </FormItem>
          <FormItem
            name="title"
            label={<Text>{t('TITLE')}</Text>}
            rules={[getRequiredRule(t('TITLE'))]}
          >
            <Input />
          </FormItem>
          <FormItem name="slotNotes" label={<Text>{t('DESCRIPTION')}</Text>}>
            <TextArea maxLength={250} showCount />
          </FormItem>
        </>
      )}
      {!hideTeamMembers && (
        <FormItem
          name="numberSupply"
          label={<Text>{t('TEAM_MEMBERS_REQUIRED')}</Text>}
          rules={[
            getRequiredRule(t('TEAM_MEMBERS_REQUIRED')),
            ...(flagsRosteringEditingLimitations
              ? [
                  {
                    ...getTeamMembersClockInsRule(
                      formData?.status,
                      formData?.clockIns
                    ),
                    validateTrigger: 'onSubmit'
                  }
                ]
              : [])
          ]}
          extra={<Text>{t('TEAM_MEMBERS_REQUIRED_SELECT_SHIFT')}</Text>}
        >
          <InputNumber
            suffix={
              <InputSuffix>{t('TEAM_MEMBERS').toLocaleLowerCase()}</InputSuffix>
            }
            min={1}
            max={50}
            style={{ width: '100%' }}
            type="number"
            formatter={cleanDecimalValues}
          />
        </FormItem>
      )}
      {showTeamMembersSelect && (
        <FormItem
          name="teamMembers"
          rules={[
            ...(flagsRosteringEditingLimitations
              ? [
                  {
                    validator: validateTeamMembers,
                    validateTrigger: ['onSubmit', 'onChange']
                  }
                ]
              : [])
          ]}
          label={<Text>{t('TEAM_MEMBER(S)')}</Text>}
        >
          <TeamMembersMultiSelect
            title={t('SELECT_TEAM_MEMBERS')}
            groupedOptions={groupedCarerOptions}
            loading={!!instanceId && isListLoading}
            formData={formData}
            teamMembersCount={teamMembersCount}
            maxTeamMembers={maxTeamMembers}
            validator={
              flagsRosteringEditingLimitations ? validateTeamMembers : undefined
            }
          />
        </FormItem>
      )}
    </FormCard>
  )
}

export default AboutShift
